import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, ConfirmModal, ErrorState } from '@react-apps/design-system';
import type {
  TotvsProductLink,
  TotvsSyncTask,
} from '../../domain/entities/totvs.entity';
import {
  BackLink,
  ErrorText,
  Field,
  formatDateTime,
  getApiErrorMessage,
  getTotvsProviderLabel,
  Mono,
  SectionCard,
  SkeletonCard,
  TotvsStatusBadge,
} from '../display';
import { TotvsAttemptsTimeline } from '../components/TotvsAttemptsTimeline';
import { TotvsPayloadEditForm } from '../components/TotvsPayloadEditForm';
import {
  useMarkTotvsSyncTaskPending,
  useProcessTotvsSyncTaskNow,
  useTotvsSyncTask,
} from '../hooks/use-totvs-sync-tasks';
import { useTotvsProductLinks } from '../hooks/use-totvs-product-links';
import { useCanWriteTotvs } from '../hooks/use-totvs-permissions';
import { EntityLink } from '@/shared/components/entity-link';

/**
 * `statusReason` que o backend carimba na task nascida retida
 * (`TOTVS_MANUAL_RELEASE_PROVIDERS`) — ver `EnqueueTotvsSyncUseCase`.
 */
const HELD_FOR_MANUAL_RELEASE_REASON = 'retido_para_liberacao_manual';

/**
 * Prefixo do motivo do job barrado pelo snapshot do `totvs-middleware` — o
 * sufixo é o desfecho de lá (`:completed`, `:failed`, `:unknown`), que é o que
 * diz se vale liberar.
 */
const HANDLED_BY_MIDDLEWARE_REASON = 'matriculado_pelo_middleware';

/** Texto do desfecho do middleware, para o operador decidir sem decorar enum. */
const MIDDLEWARE_OUTCOME_LABEL: Record<string, string> = {
  completed:
    'o middleware concluiu a matrícula — o aluno já está no RM. Liberar aqui matricula de novo.',
  failed:
    'o middleware tentou e nunca concluiu — provavelmente o aluno NÃO está no RM. Este é o caso em que liberar costuma ser o certo.',
  unknown:
    'o middleware não chegou a um desfecho conclusivo. Confira no RM antes de liberar.',
};

/**
 * Casa o de-para do jeito que o worker casa (`findMatch` do repositório):
 * gateway, **conta do gateway**, produto e oferta, com o de-para da conta
 * vencendo o curinga legado (`providerAccountId: null`), que casa qualquer
 * conta enquanto existir.
 *
 * Comparar só produto/oferta fazia a tela anunciar "de-para encontrado"
 * apontando para registro de outra conta, que o worker não usaria — o job
 * seguia em WAITING_PRODUCT_LINK com a tela dizendo o contrário. Ser mais
 * estrita que o worker seria o erro simétrico: o job matricularia por um
 * curinga que a tela diz não existir.
 */
function findMatchingProductLink(
  links: TotvsProductLink[],
  task: Pick<
    TotvsSyncTask,
    'provider' | 'providerAccountId' | 'productExternalId' | 'offerExternalId'
  >,
): TotvsProductLink | null {
  const candidates = links.filter(
    (link) =>
      link.enabled &&
      link.provider === task.provider &&
      (link.providerAccountId === task.providerAccountId ||
        link.providerAccountId === null) &&
      link.productExternalId === task.productExternalId &&
      link.offerExternalId === task.offerExternalId,
  );

  return (
    candidates.find((link) => link.providerAccountId !== null) ??
    candidates[0] ??
    null
  );
}

export function TotvsSyncTaskDetailPage() {
  const { taskId } = useParams<{ taskId: string }>();
  const canWrite = useCanWriteTotvs();
  const [processNowOutcome, setProcessNowOutcome] = useState<string | null>(
    null,
  );
  const [editingPayload, setEditingPayload] = useState(false);
  const [confirmRelease, setConfirmRelease] = useState(false);
  const { data: task, isLoading, error } = useTotvsSyncTask(taskId);
  // Best-effort só p/ exibição: casa localmente contra um lote grande de
  // de-paras (a tela de listagem já pagina o CRUD de verdade).
  const { data: productLinksPage } = useTotvsProductLinks({ perPage: 1000 });
  const markPending = useMarkTotvsSyncTaskPending();
  const processNow = useProcessTotvsSyncTaskNow();

  /** Motivo do middleware e desfecho de lá, quando o job está barrado. */
  const middlewareBlock = useMemo(() => {
    const reason = task?.statusReason;
    if (!reason?.startsWith(HANDLED_BY_MIDDLEWARE_REASON)) return null;
    const outcome = reason.slice(HANDLED_BY_MIDDLEWARE_REASON.length + 1);
    return {
      outcome,
      label:
        MIDDLEWARE_OUTCOME_LABEL[outcome] ??
        'desfecho desconhecido no middleware.',
    };
  }, [task?.statusReason]);

  const productLink = useMemo(() => {
    if (!task) return null;
    return findMatchingProductLink(productLinksPage?.items ?? [], task);
  }, [productLinksPage, task]);

  const handleMarkPending = () => {
    if (!task) return;
    setProcessNowOutcome(null);
    markPending.mutate({
      id: task.id,
      input: { reason: 'Marcado como pendente pelo detalhe do job TOTVS' },
    });
  };

  /**
   * `overrideMiddlewareGate` só existe aqui: é esta chamada que decide e chama o
   * RM na mesma execução. Marcar como pendente não aceita override porque quem
   * processaria depois é o worker, que consultaria o snapshot de novo e barraria.
   */
  const handleProcessNow = (overrideMiddlewareGate = false) => {
    if (!task) return;
    processNow.mutate(
      {
        id: task.id,
        input: overrideMiddlewareGate ? { overrideMiddlewareGate } : undefined,
      },
      { onSuccess: (result) => setProcessNowOutcome(result.outcome) },
    );
  };

  if (isLoading) {
    return (
      <div className="mx-auto max-w-4xl space-y-6 p-6">
        <BackLink to="/totvs/jobs" label="Jobs TOTVS" />
        <SkeletonCard rows={6} />
      </div>
    );
  }

  if (error || !task) {
    return (
      <div className="mx-auto max-w-4xl space-y-6 p-6">
        <BackLink to="/totvs/jobs" label="Jobs TOTVS" />
        <ErrorState
          message={getApiErrorMessage(error, 'Job TOTVS não encontrado')}
        />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-6">
      <BackLink to="/totvs/jobs" label="Jobs TOTVS" />

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-(--color-text-primary) text-2xl font-semibold">
            Job TOTVS
          </h1>
          <p className="text-(--color-text-secondary) text-sm">
            <Mono>{task.id}</Mono>
          </p>
        </div>

        {canWrite && (
          <div className="flex flex-wrap justify-end gap-2">
            <Button
              onClick={handleMarkPending}
              disabled={markPending.isPending || middlewareBlock !== null}
            >
              {markPending.isPending ? 'Marcando…' : 'Marcar como pendente'}
            </Button>
            {/* Numa task barrada este botão não teria efeito — o portão do
                middleware é reavaliado no processamento e devolveria
                `handled_by_middleware`. Então ele passa pelo modal, que é o
                único caminho com override. */}
            <Button
              variant="secondary"
              onClick={
                middlewareBlock
                  ? () => setConfirmRelease(true)
                  : () => handleProcessNow()
              }
              disabled={processNow.isPending}
            >
              {processNow.isPending ? 'Processando…' : 'Reprocessar agora'}
            </Button>
          </div>
        )}
      </div>

      {middlewareBlock && (
        <div className="rounded-lg bg-red-50 p-3 text-sm text-red-800 dark:bg-red-950/40 dark:text-red-300">
          <p>
            Este job está <strong>barrado</strong>: a assinatura consta no
            snapshot do <Mono>totvs-middleware</Mono> — {middlewareBlock.label}
          </p>
          {canWrite && (
            <p className="mt-2">
              <strong>Marcar como pendente</strong> não solta: o worker consulta
              o mesmo snapshot e barra de novo. O único caminho é{' '}
              <strong>Reprocessar agora</strong>, confirmando a matrícula
              duplicada.
            </p>
          )}
        </div>
      )}

      <ConfirmModal
        open={confirmRelease}
        onClose={() => setConfirmRelease(false)}
        onConfirm={() => {
          setConfirmRelease(false);
          handleProcessNow(true);
        }}
        title="Matricular job barrado pelo middleware"
        description={`Esta assinatura já passou pelo totvs-middleware (${middlewareBlock?.outcome ?? '—'}). Confirmar faz o SaveRecord no RM agora, nesta requisição — se o aluno já estiver matriculado, será uma matrícula duplicada. Confirma?`}
        confirmLabel="Matricular mesmo assim"
        loading={processNow.isPending}
        errorMessage={
          processNow.error
            ? getApiErrorMessage(processNow.error, 'Falha na operação')
            : null
        }
      />

      {task.statusReason === HELD_FOR_MANUAL_RELEASE_REASON && (
        <div className="rounded-lg bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
          Este job nasceu <strong>retido</strong>: o gateway dele está segurado
          enquanto a virada do middleware não fecha, então o worker nunca vai
          pegá-lo sozinho. Confira o payload abaixo e use{' '}
          <strong>Reprocessar agora</strong> para mandar a matrícula ao RM.
        </div>
      )}

      {markPending.error && (
        <ErrorText>
          {getApiErrorMessage(
            markPending.error,
            'Falha ao marcar como pendente',
          )}
        </ErrorText>
      )}
      {processNow.error && (
        <ErrorText>
          {getApiErrorMessage(processNow.error, 'Falha ao reprocessar agora')}
        </ErrorText>
      )}
      {processNowOutcome && (
        <p className="text-(--color-text-secondary) text-sm">
          Retorno do reprocessamento imediato: <Mono>{processNowOutcome}</Mono>
        </p>
      )}

      <SectionCard title="Status e origem">
        <div className="grid gap-4 md:grid-cols-3">
          <Field
            label="Status"
            value={<TotvsStatusBadge status={task.status} />}
          />
          <Field label="Tentativas falhas" value={task.failedAttempts} />
          <Field
            label="Nesse status desde"
            value={formatDateTime(task.inCurrentStatusSince)}
          />
          <Field
            label="Contrato"
            value={
              <Mono>
                <EntityLink kind="contract" id={task.contractId} />
              </Mono>
            }
          />
          <Field
            label="Usuário"
            value={
              <Mono>
                <EntityLink kind="user" id={task.userId} />
              </Mono>
            }
          />
          <Field
            label="Transação"
            value={
              <Mono>
                <EntityLink kind="transaction" id={task.transactionId} />
              </Mono>
            }
          />
          <Field label="Invoice" value={<Mono>{task.invoiceId}</Mono>} />
          <Field
            label="Última tentativa"
            value={formatDateTime(task.lastAttemptAt)}
          />
          <Field label="Atualizado em" value={formatDateTime(task.updatedAt)} />
        </div>
        <Field label="Motivo" value={task.statusReason} />
        <Field
          label="Último erro"
          value={
            task.lastError ? (
              <span className="text-red-600 dark:text-red-400">
                {task.lastError}
              </span>
            ) : null
          }
        />
      </SectionCard>

      <SectionCard title="Produto e de-para">
        <div className="grid gap-4 md:grid-cols-2">
          <Field label="Gateway" value={getTotvsProviderLabel(task.provider)} />
          <Field
            label="Conta do gateway"
            value={
              task.providerAccountId ? (
                <Mono>{task.providerAccountId}</Mono>
              ) : (
                '—'
              )
            }
          />
          <Field
            label="Produto externo"
            value={<Mono>{task.productExternalId}</Mono>}
          />
          <Field
            label="Oferta externa"
            value={<Mono>{task.offerExternalId}</Mono>}
          />
          <Field label="Nome da oferta" value={task.offerName} />
          <Field
            label="De-para encontrado"
            value={
              productLink ? (
                <Mono>{productLink.id}</Mono>
              ) : (
                'Nenhum de-para ativo encontrado'
              )
            }
          />
          {productLink?.action === 'ignore' && (
            <Field
              label="Destino"
              value={`Ignorar — ${productLink.ignoreReason ?? 'sem justificativa registrada'}`}
            />
          )}
          {productLink?.action === 'enroll' && (
            <>
              <Field
                label="IDAREAINTERESSE"
                value={<Mono>{productLink.idAreaInteresse}</Mono>}
              />
              <Field label="CODCOLIGADA" value={productLink.codColigada} />
              <Field label="CODFILIAL" value={productLink.codFilial} />
              <Field label="IDPS" value={productLink.idPs} />
            </>
          )}
        </div>
      </SectionCard>

      <SectionCard
        title="Payload ZMDSALUNO"
        action={
          canWrite &&
          !editingPayload && (
            <Button variant="secondary" onClick={() => setEditingPayload(true)}>
              Editar
            </Button>
          )
        }
      >
        {editingPayload ? (
          <TotvsPayloadEditForm
            taskId={task.id}
            payload={task.payload}
            onDone={() => setEditingPayload(false)}
          />
        ) : task.payload ? (
          <pre className="border-(--color-border) bg-(--color-bg-secondary) max-h-96 overflow-auto rounded-lg border p-4 text-xs">
            {JSON.stringify(task.payload, null, 2)}
          </pre>
        ) : (
          <p className="text-(--color-text-secondary) text-sm">
            Payload ainda não foi montado. Isso é esperado quando o job está
            aguardando cadastro do produto ou falhou antes do mapeamento.
          </p>
        )}
      </SectionCard>

      <SectionCard title="Tentativas no RM">
        <TotvsAttemptsTimeline attempts={task.attempts} />
      </SectionCard>
    </div>
  );
}
